<template>
  <div class="background">
    <div v-if="showSpinner" class="spinner-container">
      <spinner-el class="spinner" />
    </div>
    <template v-else>
      <div class="container subscription">
        <head-title class="head-title">Subscription</head-title>
        <div class="subscription-item subscription-item_plan">
          <div class="subscription__title subscription__column subscription__column_left subscription__column_left_plan">
            Your Plan
          </div>
          <div class="subscription__column subscription__column_right">
            <div class="subscription__info-box">
              <div class="subscription__info">
                <div class="subscription__title">
                  Cryptoalerts <span class="uppercase">{{currentTariff.nickname}}</span>
                </div>
                <div
                  v-if="futureTariff"
                  class="subscription__start-date"
                  :title="quotasInfo.currentTariffPlanEndDate | format(dateTimeFormat)"
                >
                  Active until {{quotasInfo.currentTariffPlanEndDate  | format(dateFormat)}}
                </div>
                <div
                  v-else-if="!currentTariffIsFree"
                  :title="quotasInfo.currentTariffPlanEndDate | format(dateTimeFormat)"
                  class="subscription__label subscription__label_plan"
                >
                  Ends at {{quotasInfo.currentTariffPlanEndDate | format(dateFormat)}}
                </div>
              </div>
              <router-link
                to="/subscription/tariffs/"
                class="subscription__change-btn"
                v-if="!attemptToChargeMoney"
                :class="{'subscription__change-btn_disabled': tariffWillBeChangedAt}"
              >
                <button-el red :disabled="!!tariffWillBeChangedAt">Change</button-el>
              </router-link>
            </div>
            <red-label
              v-if="attemptToChargeMoney"
              class="red-label red-label_final-confirmation"
            >Waiting for the final confirmation of&nbsp;the transfer (up&nbsp;to&nbsp;1&nbsp;hour)</red-label>
            <red-label v-if="futureTariff" class="tariff-will-be-changed red-label">
              <span class="red-label__text">
                <span>Your plan will be&nbsp;switched to&nbsp;{{futureTariff.nickname}} on&nbsp;</span><span class="disable-word-wrap" :title="tariffWillBeChangedAt | format(dateTimeFormat)">{{tariffWillBeChangedAt | format(shortMonthAndDayDateFormat)}}</span>
              </span>
              <button-el red class="tariff-will-be-changed__btn" @click="declineTariffPlanChanging" :disabled="disableCancelPlanedTariffBtn">Cancel</button-el>
            </red-label>
          </div>
        </div>

        <div class="subscription-item subscription-item_limits">
          <div class="subscription__title subscription__column_left">
            Limits
          </div>
          <div class="subscription__column subscription__column_right subscription__column_right_limits">
            <div class="limits">
              <div class="limit">
                <div class="limit__title">SMS</div>
                <div class="limit__balance">{{quotasInfo.balance.sms}} / {{quotasInfo.limits.sms}}</div>
              </div>
              <div class="limit">
                <div class="limit__title">Email</div>
                <div class="limit__balance">{{quotasInfo.balance.email}} / {{quotasInfo.limits.email}}</div>
              </div>
              <dir class="limit__separator"></dir>
              <div class="limit">
                <div class="limit__title">Push</div>
                <div class="limit__balance">{{quotasInfo.balance.push}} / {{quotasInfo.limits.push}}</div>
              </div>
              <div class="limit">
                <div class="limit__title">Webhooks</div>
                <div class="limit__balance">{{quotasInfo.balance.webhook}} / {{quotasInfo.limits.webhook}}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="subscription-item subscription-item_information">
          <div class="subscription__title subscription__column subscription__column_left">
            Payment Information
          </div>
          <div class="subscription__column subscription__column_payment subscription__column_right">
            <template v-if="cardInfo && cardInfo.lastDigits">
              <div class="last-payment-info" v-if="cardInfo.lastPaymentDate">
                <div class="last-payment-info__col">
                  <div class="last-payment-info__col-title">Payment Date</div>
                  <div class="last-payment-info__col-val"
                    :title="cardInfo.lastPaymentDate | format(dateTimeFormat)"
                  >{{cardInfo.lastPaymentDate | format(shortDateFormat)}}</div>
                </div>
                <div class="last-payment-info__col">
                  <div class="last-payment-info__col-title">Amount</div>
                  <div class="last-payment-info__col-val">{{cardInfo.tariffPrice | formatMoney}}</div>
                </div>
              </div>
              <div class="payment-field">
                <icon-base black class="icon-card">
                  <component :is="cardInfo.brandName | getCardIcon" />
                </icon-base>
                <div class="card-number">
                  **** **** **** {{cardInfo.lastDigits}}
                </div>
                <div class="btns-wrapper">
                  <router-link
                    to="/subscription/link-card"
                    class="update-btn-link"
                    :class="{'update-btn-link_disabled': requestInProgress}"
                  >
                    <button-el red class="update-btn" :disabled="requestInProgress">Update</button-el>
                  </router-link>
                  <round-btn
                    small
                    class="btn-unlink"
                    @click="removePaymentCard"
                    :disabled="requestInProgress"
                  >
                    <icon-base red>
                      <icon-cross />
                    </icon-base>
                  </round-btn>
                </div>
              </div>
            </template>
            <div class="card-isnt-set payment-field" v-else>
              <div class="card-isnt-set__icon"></div>
              <div class="card-isnt-set__text">Payment is not verified yet</div>
              <router-link class="btn-link" to="/subscription/link-card">
                <button-el red>Link Card</button-el>
              </router-link>
            </div>
          </div>
        </div>

      </div>
      <payments-table componentMode />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { format } from 'date-fns';
import { IconCross } from 'Icons';
import subscriptionPlans from 'src/models/SubscriptionPlans';
import paymentsTable from 'Common/PaymentsTable';
import { formatMoney, formatCountEntries, getCardIcon } from 'src/utils/helpers';
import { dateFormat, shortDateFormat, dateTimeFormat, shortMonthAndDayDateFormat } from 'src/utils/config';
import NotificationsKeys from 'src/models/NotificationsKeys';

export default {
  components: {
    IconCross,
    paymentsTable,
  },
  filters: {
    formatMoney,
    format,
    formatCountEntries,
    getCardIcon,
  },
  data: () => ({
    cardInfo: null,
    showSpinner: true,
    requestInProgress: false,
    disableCancelPlanedTariffBtn: false,
  }),
  created() {
    this.updatePageData()
      .then(() => {
        this.showSpinner = false;
      });
  },
  methods: {
    ...mapActions('subscription', ['updateTariffs', 'updateQuotasInfo', 'updateCurrentTariff']),
    getPaymentCardInfo() {
      return this.$apiService.getPaymentCardInfo().then((cardInfo) => {
        this.cardInfo = cardInfo;
      });
    },
    removePaymentCard() {
      if (confirm('Are you sure?')) { // eslint-disable-line
        this.requestInProgress = true;
        this.$apiService.removePaymentCard().finally(() => {
          this.getPaymentCardInfo().finally(() => {
            this.requestInProgress = false;
          });
        });
      }
    },
    declineTariffPlanChanging() {
      this.disableCancelPlanedTariffBtn = true;
      this.$apiService.declineTariffPlanChanging().finally(() => {
        this.updatePageData().finally(() => {
          this.disableCancelPlanedTariffBtn = false;
        });
      });
    },
    updatePageData() {
      return Promise.all([
        this.updateQuotasInfo(),
        this.getPaymentCardInfo(),
        this.updateTariffs(),
        this.updateCurrentTariff(),
      ]);
    },
  },
  computed: {
    ...mapState('subscription', [
      'tariffs',
      'quotasInfo',
      'currentTariff',
      'futureTariff',
      'tariffWillBeChangedAt',
    ]),
    ...mapState('userNotifications', ['userNotifications']),
    dateFormat: () => dateFormat,
    dateTimeFormat: () => dateTimeFormat,
    shortDateFormat: () => shortDateFormat,
    shortMonthAndDayDateFormat: () => shortMonthAndDayDateFormat,
    currentTariffIsFree() {
      return this.currentTariff && this.currentTariff.type === subscriptionPlans.free;
    },
    attemptToChargeMoney() {
      return this.cardInfo.status === 'pending';
    },
  },
  watch: {
    userNotifications() {
      for (let i = 0; i < this.userNotifications.length; i += 1) {
        const notification = this.userNotifications[i];
        if (notification.notificationKey === NotificationsKeys.chargeIsFailedFirstTime ||
            notification.notificationKey === NotificationsKeys.chargeIsFailedSecondTime ||
            notification.notificationKey === NotificationsKeys.emailLowBalance ||
            notification.notificationKey === NotificationsKeys.smsLowBalance ||
            notification.notificationKey === NotificationsKeys.pushLowBalance ||
            notification.notificationKey === NotificationsKeys.webhookLowBalance ||
            notification.notificationKey === NotificationsKeys.tariffWasActivated ||
            notification.notificationKey === NotificationsKeys.paymentIsUnsuccess ||
            notification.notificationKey === NotificationsKeys.paymentIsSuccess) {
          this.updatePageData();
          break;
        }
      }
    },
  },
};
</script>


<style lang="scss" scoped>
  .card-number {
    &_intable {
      display: inline-block;
      vertical-align: bottom;
    }
  }
  .background {
    @include background;
  }
  $container-width: 1200px;
  $side-padding: 10px;
  .container {
    @include container;
    width: calc(100% - #{$side-padding * 2});
    background: $white-color2;
    border-radius: 5px;
    max-width: $container-width;
  }

  .head-title {
    padding: 31px 30px;
    border-bottom: 1px solid $grey-color3;
  }

  .subscription-item {
    padding: 37px 0 0 30px;
    display: flex;
    &_limits {
      padding: 42px 0 0 30px;
      .subscription__column {
        padding-bottom: 35px;
      }
    }
    &_information {
      padding: 39px 0 0 30px;
      .subscription__column {
        border-bottom: none;
      }
    }
  }

  .btns-wrapper {
    display: flex;
    align-items: center;
  }

  .subscription {
    &__title {
      @include font(17px, $black-color1, 500);
      line-height: 20px;
    }

    &__label {
      @include font(13px, $grey-color9, 500);
      line-height: 21px;
      &_plan {
        padding: 6px 0 0;
      }
    }

    &__column_left {
      width: 270px;
    }

    &__column_right {
      border-bottom: $common-border;
      padding-bottom: 39px;
      flex: 1;
      padding-right: 30px;
    }

    &__start-date {
      @include font(13px, $grey-color9, 500);
      margin-top: 9px;
      margin-bottom: 15px;
    }
    &__info-box {
      display: flex;
    }
    &__change-btn {
      width: 150px;
      margin-left: 61px;
      &_disabled {
        pointer-events: none;
      }
    }
  }

  $limits-margin-left: 55px;
  $limits-margin-bottom: 29px;
  .limits {
    margin-top: 5px;
    margin-left: -4px;
    display: flex;
    flex-wrap: wrap;
    margin-left: -$limits-margin-left;
    margin-bottom: -$limits-margin-bottom;
  }

  .limit {
    margin-left: $limits-margin-left;
    margin-bottom: $limits-margin-bottom;
    &__title {
      @include font(12px, $grey-color1, 500);
    }
    &__balance {
      @include font(18px, $black-color1, 500);
      padding-top: 10px;
      white-space: nowrap;
    }
    &__separator {
      padding: 0;
      margin: 0;
    }
  }

  .red-label {
    margin-bottom: 1px;
    margin-top: 5px;
    max-width: 640px;
    display: flex;
    align-items: center;
  }
  .last-payment-info {
    text-align: left;
    margin-top: 5px;
    margin-left: -1px;
    margin-bottom: 36px;
    display: flex;
    &__col:not(:last-of-type) {
      margin-right: 45px;
    }
    &__col-val {
      @include font(13px);
      padding-top: 9px;
    }

    &__col-title {
      @include font(12px, $grey-color1, 500);
    }
  }

  .red-label {
    margin-top: 34px;
    max-width: 640px;
    display: flex;
    align-items: center;
    &_final-confirmation {
      max-width: 494px;
      margin-top: 16px;
      line-height: 20px;
    }
  }

  .update-btn {
    vertical-align: top;
    position: relative;
    width: 150px !important;
  }

  .update-btn-link {
    position: relative;
    left: 3px;
    &_disabled {
      pointer-events: none;
    }
  }

  .payment-field {
    display: flex;
    align-items: center;
    padding: 25px 30px;
    max-width: 470px;
    @include customBorder(3px, 1px, 0);
    border-radius: 4px;
    &:first-child {
      margin-top: 4px;
    }
    .card-number {
      margin-left: 26px;
      margin-right: 23px;
      @include font(14px, $black-color1, 500);
      line-height: 23px;
    }
    .icon-card {
      width: 48px;
      margin-right: 1px;
    }
  }
  .card-isnt-set {
    width: 470px;
    justify-content: space-between;
    &__icon {
      background: url("~src/assets/svg/card.svg");
      width: 30px;
      height: 21px;
    }
    &__text {
      @include font(14px, $grey-color1, 500);
      line-height: 23px;
      margin-left: -14px;
    }
  }
  .btn-link {
    display: inline-block;
    width: 100%;
    max-width: 150px;
  }
  .btn-unlink {
    margin-left: 18px;
  }
  .spinner {
    margin: auto;
  }
  .tariff-will-be-changed {
    .red-label__text {
      line-height: 18px;
    }
    &__btn {
      margin-left: 39px;
      margin-right: 0;
    }
  }

  @media screen and (max-width: 1044px) {
    .subscription {
      padding-bottom: 21px;
      &__column {
        &_left {
          margin-bottom: 29px;
          &_plan {
            display: none;
          }
        }
        &_right {
          border-bottom: none;
        }
      }
    }
    .subscription-item {
      flex-direction: column;
      &:not(:last-child) {
        border-bottom: $common-border;
      }
      &_information,
      &_limits {
        padding-top: 36px;
      }
    }
    .last-payment-info {
      margin-bottom: 28px;
    }
    .payment-history {
      @include incrSelectorWeight(1) {
        margin-top: 30px;
      }
    }
  }
  @media screen and (max-width: 1024px) {
    .background {
      padding-top: 10px;
    }
  }
  @media screen and (max-width: 600px) {
    .limit__separator {
      width: 100%;
    }
  }
  @media screen and (max-width: 582px) {
    .tariff-will-be-changed {
      flex-direction: column;
      text-align: center;
      &__btn {
        margin: 28px 0 0;
      }
    }
    .payment-field {
      flex-wrap: wrap;
    }
    .red-label {
      &_final-confirmation {
        text-align: center;
      }
    }
  }
  @media screen and (max-width: 564px) {
    .btns-wrapper {
      margin-top: 23px;
      width: 100%;
    }
    .payment-field {
      padding-top: 29px;
      padding-bottom: 29px;
      max-width: 252px;
      flex-direction: column;
      .icon-card {
        margin: 1px 0 15px;
      }
      .card-number {
        margin-right: 0;
        margin-left: 0;
      }
    }
    .card-isnt-set {
      &__icon {
        margin-bottom: 16px;
      }
      &__text {
        margin-left: 0;
        margin-bottom: 24px;
      }
    }
    .subscription__column_payment {
      padding: 0;
    }
    .subscription-item_information {
      align-items: center;
      text-align: center;
      padding-left: 0;
    }
    .subscription {
      padding-bottom: 40px;
    }
    .last-payment-info {
      text-align: center;
      justify-content: center;
    }
    .subscription-item_limits {
      text-align: center;
      align-items: center;
    }
    .limits {
      justify-content: center;
    }
    .subscription-item_limits {
      padding-left: 0;
    }
    .subscription__column_right_limits {
      padding-right: 0;
    }
  }
  @media screen and (max-width: 482px) {
    .subscription {
      &__info-box {
        flex-direction: column;
        text-align: center;
        align-items: center;
        margin-bottom: -4px;
      }
      &__change-btn {
        margin-left: 0;
        margin-top: 16px;
      }
      $side-margin: 15px;
      &_plan {
        padding-left: 0;
      }
      &_limits,
      &_plan {
        .subscription {
          &__column {
            &_right {
              padding-left: $side-margin;
              padding-right: $side-margin;
            }
          }
        }
      }
    }
    .container {
      $side-padding: 5px;
      width: calc(100% - #{$side-padding * 2});
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/subscription/Managment.vue